import React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "@/lib/utils";

// Folder-specific sidebar components that are optimized for folder structure usage

const FolderSidebarMenuSubItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<"li"> & {
    level?: number;
  }
>(({ className, level = 0, ...props }, ref) => (
  <li 
    ref={ref} 
    className={cn(
      "list-none",
      level > 0 && `ms-${Math.min(level * 4, 16)}`, // Indent based on level, max 16 (start margin for RTL support)
      className
    )}
    {...props} 
  />
));
FolderSidebarMenuSubItem.displayName = "FolderSidebarMenuSubItem";

const FolderSidebarMenuSubButton = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentProps<"a"> & {
    asChild?: boolean;
    size?: "sm" | "md";
    isActive?: boolean;
    level?: number;
    isDraggedOver?: boolean;
  }
>(({ 
  asChild = false, 
  size = "md", 
  isActive, 
  level = 0,
  isDraggedOver = false,
  className, 
  ...props 
}, ref) => {
  const Comp = asChild ? Slot : "a";

  return (
    <Comp
      ref={ref}
      data-sidebar="folder-menu-sub-button"
      data-size={size}
      data-active={isActive}
      data-level={level}
      data-dragged-over={isDraggedOver}
      className={cn(
        // Base styles from SidebarMenuSubButton
        "flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-sm px-2 text-sidebar-foreground outline-none ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-sidebar-accent-foreground",
        // Active state
        "data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground",
        // Size variations
        size === "sm" && "text-xs",
        size === "md" && "text-sm",
        // Folder-specific styles
        "group-data-[collapsible=icon]:hidden relative w-full justify-start transition-colors",
        // Drag and drop styling
        isDraggedOver && "bg-blue-100 border-2 border-blue-300 border-dashed",
        // Level-based indentation (using padding instead of margin for better click area)
        level > 0 && `ps-${Math.min(2 + level * 2, 8)}`, // Progressive indentation (start padding for RTL support)
        // Ensure full width to right edge
        "w-full",
        className
      )}
      {...props}
    />
  );
});
FolderSidebarMenuSubButton.displayName = "FolderSidebarMenuSubButton";

const FolderSidebarMenuButton = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & {
    asChild?: boolean;
    isActive?: boolean;
    size?: "default" | "sm" | "lg";
    variant?: "default" | "outline";
    isDraggedOver?: boolean;
  }
>(({
  asChild = false,
  isActive = false,
  size = "default",
  variant = "default",
  isDraggedOver = false,
  className,
  ...props
}, ref) => {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      ref={ref}
      data-sidebar="folder-menu-button"
      data-active={isActive}
      data-size={size}
      data-variant={variant}
      data-dragged-over={isDraggedOver}
      className={cn(
        // Base styles from SidebarMenuButton
        "peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-sm p-2 text-start text-sm outline-none ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-2 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0",
        // Active state
        "data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground",
        // Size variations
        size === "sm" && "text-xs",
        size === "lg" && "text-base",
        // Folder-specific styles
        "relative w-full justify-start transition-colors",
        // Drag and drop styling
        isDraggedOver && "bg-blue-100 border-2 border-blue-300 border-dashed",
        className
      )}
      {...props}
    />
  );
});
FolderSidebarMenuButton.displayName = "FolderSidebarMenuButton";

export {
  FolderSidebarMenuSubItem,
  FolderSidebarMenuSubButton,
  FolderSidebarMenuButton,
};

